import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { formatFaps } from "@gooner/shared";
import { useSession } from "../../lib/session";
import { trackGoldImpression, useGoldGate } from "../../lib/gold";
import { trackFapsPillOpened, useFaps } from "../../lib/faps";
import { cx } from "../ui/primitives";
import { GoldChip } from "../ui/Gold";
import { CROWN_GOLD, CrownMark } from "../ui/Crown";
import { Avatar } from "../ui/Avatar";
import { MENU_ROW, MenuRowIcon } from "../../components/SiteBar";
import { IconSignIn, IconSignOut, IconSliders, IconUpload, IconUser } from "../ui/icons";
import { usePublicSettings } from "../../lib/publicSettings";
import { NewBadge } from "../../components/NewBadge";

/** The Upload row wears "New!" until this date — the vault launched 2026-09-09, and a
 *  month is how long a menu row stays news. Same rule as the Infinite Scroll card and
 *  the Daily Quests header; see NewBadge for why the dates are not shared. */
const UPLOAD_NEW_UNTIL = Date.UTC(2026, 9, 10);

/**
 * Who you are, as MENU ROWS — the ☰'s top group at every width. The standalone
 * AccountControl bubble (and its AccountMenu dropdown) is DELETED: the site bar carries
 * one menu now, and a second who-am-I surface beside it was the two-menus problem the
 * unification exists to end. This file is the ONLY place the signed-out/signed-in fork
 * may be written — that rule survives the bubble it used to share the file with.
 *
 * It does NOT own the AuthModal, and that is the bug fix, not an inconsistency with
 * AccountControl above. These rows live inside SiteMenu's `{open && …}` dropdown, and
 * the menu unmounts on any document-level mousedown outside its own DOM node — which the
 * portaled modal is (it renders into document.body). Owning the modal here meant the
 * FIRST tap inside the dialog killed the menu, the menu's unmount took the modal's state
 * with it, and the dialog vanished before the tap's click ever landed. So the row only
 * reports the tap (`onSignIn`); the modal lives with whoever renders the chrome, above
 * the menu, where closing the menu cannot reach it. The menu closing on this tap is now
 * CORRECT rather than fatal, so the old stopPropagation went with the state.
 */
/**
 * The GoonLords Gold menu row, shared by BOTH forks (the file's two-copies rule) —
 * the self-serve door to the Gold dialog, and the lapse-recovery nudge.
 *
 * Under healthy rebilling the "ends soon" window rarely shows: a renewal extends
 * gold_until before it arrives. The nudge exists for the three ways it doesn't — a
 * failed payment, a cancellation running out, a manual grant ending. Rendering the row
 * is an impression (session-deduped, the screen_code_shown precedent); clicking is the
 * CTA. Hidden entirely while selling is off and the user never had Gold, so the row
 * cannot advertise a pitch that says "almost here" to someone who never asked.
 */
function GoldMenuRow({ row, onOpen }: { row: string; onOpen: (source: "menu" | "lapse") => void }) {
  const { gold, goldUntil, sellingEnabled } = useGoldGate();
  const lapsed = goldUntil != null && goldUntil <= Date.now();
  const endingSoon = gold && (goldUntil ?? 0) < Date.now() + 7 * 24 * 60 * 60 * 1000;
  const nudge = lapsed || endingSoon;
  /* SIGNED OUT SHOWS TOO (while selling is on): the row is the tier's one standing
     advertisement, and hiding it from exactly the people who don't have it was
     backwards. The dialog it opens already carries the signed-out truth — no buy
     section, "Gold lives on an account", a sign-in button. */
  const show = sellingEnabled || goldUntil != null;
  useEffect(() => {
    if (show && nudge) trackGoldImpression("lapse");
    else if (show && !gold) trackGoldImpression("menu");
  }, [show, nudge, gold]);
  if (!show) return null;
  return (
    <button type="button" role="menuitem" onClick={() => onOpen(nudge ? "lapse" : "menu")} className={row}>
      {/* ONE MARK IN THE ICON COLUMN, whichever variant this is — the amber dot when the
          row is a nudge, the badge when it is the standing offer. Two would put the alert
          and the identity in the same slot fighting for it, and the column is 20px wide.

          The badge LEADS the row now rather than trailing "Premium", which is where every
          other row in this menu wears its glyph — and it is still UNCONDITIONAL, unlike
          everywhere else the mark appears. Beside a username the badge is a status: this
          person pays. Here it is part of the row's NAME, and a crown that came and went
          depending on the reader's own tier would make the menu itself look unstable. */}
      <MenuRowIcon>
        {nudge ? (
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        ) : (
          <GoldChip />
        )}
      </MenuRowIcon>
      {lapsed ? (
        "Your Premium lapsed — renew"
      ) : endingSoon ? (
        <>Premium ends {new Date(goldUntil as number).toLocaleDateString()} — renew</>
      ) : (
        /* The lapse variants name the tier the same way, or the row would rename the
           product the moment a payment failed. */
        "Premium"
      )}
    </button>
  );
}

/**
 * The FAPS row — the balance, and whether the chest is waiting. Signed-in fork ONLY:
 * the pill in the bar is the standing nudge for everyone else, and a second
 * advertisement in the menu is the two-surfaces problem this file exists to end.
 * Reports the tap like the rows around it; the dialog lives above the menu.
 *
 * That "in the bar" now means `sm` AND UP — the pill is hidden on a phone, both forks
 * of it (see FapsPill). So below `sm` this row is the only Faps surface there is, and
 * FapsEarnMenuRow below is its signed-out twin for the same width. The two-surfaces
 * rule survives because the widths are disjoint: the pill above `sm`, a menu row below
 * it, never both.
 */
function FapsMenuRow({ row, onOpen }: { row: string; onOpen: () => void }) {
  const { enabled, balance, chestReady } = useFaps();
  if (!enabled) return null;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        trackFapsPillOpened("menu");
        onOpen();
      }}
      className={row}
    >
      {/* One mark in the column, GoldMenuRow's rule: the amber dot while there is a
          chest to open, the crown otherwise. */}
      <MenuRowIcon>
        {chestReady ? <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> : <CrownMark size={14} />}
      </MenuRowIcon>
      Faps
      {chestReady ? (
        <span className="ml-2 rounded-full bg-amber-400/20 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
          Chest
        </span>
      ) : null}
      <span className="ml-auto pl-3 tabular-nums font-semibold" style={{ color: CROWN_GOLD }}>
        {balance === null ? "····" : formatFaps(balance)}
      </span>
    </button>
  );
}

/**
 * The FAPS row's SIGNED-OUT twin — the "Earn" nudge, in the menu, on a phone only.
 *
 * `sm:hidden` is the whole point and the reason this does not break the two-surfaces
 * rule the file above states. At `sm` and up the pill still carries this nudge in the
 * bar, so a row here would be the second advertisement; below `sm` the pill is hidden in
 * both its forks (see FapsPill) and this row is the FIRST one — the only Faps door a
 * signed-out phone has. The widths are disjoint, so exactly one of the two exists at any
 * viewport, which is what the rule actually asks for.
 *
 * Same door as the pill's, not merely a similar one: register mode, and FAPS_AUTH_REASON
 * as the line above the form, so the sign-up that Faps prompted still says so. Reports
 * the tap upward like every other row — the menu unmounts on the tap and cannot own a
 * modal.
 */
function FapsEarnMenuRow({ row, onEarn }: { row: string; onEarn: () => void }) {
  const { enabled } = useFaps();
  if (!enabled) return null;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        trackFapsPillOpened("menu");
        onEarn();
      }}
      className={cx(row, "sm:hidden")}
    >
      <MenuRowIcon>
        <CrownMark size={14} tone="muted" />
      </MenuRowIcon>
      Earn Faps
    </button>
  );
}

export function MenuAccountItems({
  onSignIn,
  onGold,
  onFaps,
  onEarnFaps,
}: {
  onSignIn: () => void;
  /** Reported up like onSignIn: the menu unmounts on the tap, so the dialog must live
   *  with whoever renders the chrome — owning it here is the exact bug AuthModal had. */
  onGold: (source: "menu" | "lapse") => void;
  /** The FAPS row's tap, reported up for the same reason. */
  onFaps: () => void;
  /** The signed-out Earn row's tap. A SEPARATE callback from onSignIn because it is a
   *  different door: register rather than login, and carrying FAPS_AUTH_REASON — the
   *  same one the pill opens at wider viewports. */
  onEarnFaps: () => void;
}) {
  const { user, ready, logout } = useSession();
  // Read before the early return below — a hook after a conditional return is the bug
  // React warns about, and a menu that flickers rows is how you would notice it.
  const vaultOn = usePublicSettings()?.vault?.enabled ?? false;
  // The menu's one row shape, from the menu itself — these rows sit in the same popover as
  // the section links and the page's own, and they are one list, not three.
  const row = cx(MENU_ROW, "text-zinc-300");

  if (!ready) return null;
  return (
    <>
      {user ? (
        <>
          {/* The money you HAVE leads the money you could buy: FAPS (absent while dark),
              then Gold's sell or lapse nudge. */}
          <FapsMenuRow row={row} onOpen={onFaps} />
          <GoldMenuRow row={row} onOpen={onGold} />
          <NavLink to="/profile" role="menuitem" className={row}>
            <MenuRowIcon>
              {/* Their own face once they set one (2026-09-13) — the same Avatar every
                  surface wears, at the row's icon size. */}
              {user.avatarUrl ? <Avatar name={user.username} src={user.avatarUrl} size="sm" className="!h-4 !w-4" /> : <IconUser />}
            </MenuRowIcon>
            Profile
          </NavLink>
          {/* Account settings, under Profile: the two are the same person's page, one
              for what they did and one for what they set. A NavLink and never a dialog —
              SiteMenu unmounts on any outside mousedown, and a portaled modal is outside
              by definition, which is why the menu's modals are owned by SiteChrome. */}
          <NavLink to="/settings" role="menuitem" className={row}>
            <MenuRowIcon>
              <IconSliders />
            </MenuRowIcon>
            Settings
          </NavLink>
          {/* The vault's door, under Profile: the page is the "Your Uploads" collection,
              reached here by the thing you DO there rather than by what it is called.
              Absent while the vault is dark — a row to a 404 is worse than no row. */}
          {vaultOn ? (
            <NavLink to="/collections/uploads" role="menuitem" className={row}>
              <MenuRowIcon>
                <IconUpload />
              </MenuRowIcon>
              Upload
              {Date.now() < UPLOAD_NEW_UNTIL ? <NewBadge variant="inline" /> : null}
            </NavLink>
          ) : null}
          <button type="button" role="menuitem" onClick={() => void logout()} className={row}>
            <MenuRowIcon>
              <IconSignOut />
            </MenuRowIcon>
            Sign out
          </button>
        </>
      ) : (
        <>
          {/* Premium leads here too — same top slot as the signed-in fork, so the menu
              reads the same whoever opens it. */}
          <GoldMenuRow row={row} onOpen={onGold} />
          {/* The phone's only Faps door, under Premium so the money rows stay adjacent
              in both forks. Absent while FAPS is dark, and above `sm` where the pill
              carries the nudge instead. */}
          <FapsEarnMenuRow row={row} onEarn={onEarnFaps} />
          {/* ONE row — the modal's own toggle reaches "create an account", same as the
              bar's one button. The door's arrow is the only thing that separates this
              glyph from Sign out's, which is the point: same act, opposite direction. */}
          <button type="button" role="menuitem" onClick={onSignIn} className={row}>
            <MenuRowIcon>
              <IconSignIn />
            </MenuRowIcon>
            Sign in
          </button>
        </>
      )}
    </>
  );
}
